import type { components } from '../../types/billingRecurly.d';
import {
  ADDON_CREDIT_BLOCKS,
  CREDITS_PER_BLOCK,
  DEVICE_TYPE_TO_LABEL,
  FLAT,
  PLAN_INTERVAL,
  PRODUCT_API_TESTING,
  PRODUCT_LIVE,
  PRODUCT_MOBILE_QUALITY,
  PRODUCT_RDC,
  PRODUCT_VDC,
  REAL_DEVICE,
  STAIRSTEP,
  TESTING_TYPES,
  TESTING_TYPE_TO_LABEL,
  UNIFIED_BILLING_TYPE,
  USAGE_TYPE,
  VIRTUAL_DEVICE,
} from '../constants';
import { creditsConsideringAnnualBonus } from '../ducks/plans/utils';

export type ServerPlan = { [key in keyof components['schemas']['Plan']]-?: Exclude<components['schemas']['Plan'][key], undefined> };

export type ServerPlanAddon = components['schemas']['PlanAddon'];

interface Plan extends ServerPlan {
  // eslint-disable-next-line camelcase
  add_ons: PlanAddOn[];
}

/**
 * Returns product type of plan ("vdc" | "rdc" | "live") works for legacy and unified
 * @returns {string} Type of product
 */
const getProductType = (plan: Plan): ProductType => {
  const isVirtualDevice = plan.device_type === VIRTUAL_DEVICE;
  const isAutomated = plan.testing_type === TESTING_TYPES.AUTOMATED_LIVE;
  if (plan.testing_type === TESTING_TYPES.LIVE) return PRODUCT_LIVE;
  if (plan.testing_type === TESTING_TYPES.MOBILE_QUALITY) return PRODUCT_MOBILE_QUALITY;
  if (plan.testing_type === TESTING_TYPES.API_TESTING) return PRODUCT_API_TESTING;
  if (isAutomated && isVirtualDevice) return PRODUCT_VDC;
  if (isAutomated && plan.device_type === REAL_DEVICE) return PRODUCT_RDC;
  if (plan.isUsage()) return USAGE_TYPE;
  throw Error(`Unknown product type ${String(plan.testing_type)}, ${String(plan.device_type)}`);
};

class PlanAddOn implements ServerPlanAddon {
  constructor({ tiers, ...attributes }: Partial<ServerPlanAddon> = {}) {
    Object.assign(this, { ...attributes });
    this.tiers = tiers?.sort((left, right) => left.ending_quantity - right.ending_quantity);
  }

  /**
    * Calculates the price of the plan according to given tiered pricing model.
    * @param {number} addonQuantity
    * @returns {number} Calculated price in USD.
    */
  calculatePrice(addonQuantity: number): number {
    if (this.tier_type === FLAT) {
      return addonQuantity * (this.unit_amount as number);
    }

    if (this.tier_type === STAIRSTEP) {
      return Math.min(
        ...this?.tiers
          .filter((tier) => addonQuantity <= tier.ending_quantity)
          .map((tier) => tier.currencies[0].unit_amount), // there's always only one currency (USD)
      );
    }

    let total = 0;
    let previousEndingQuantity = 0;
    this.tiers?.forEach((tier) => {
      if (previousEndingQuantity < addonQuantity) {
        total += (
          Math.min(addonQuantity, tier.ending_quantity) - previousEndingQuantity
        ) * tier.currencies[0].unit_amount; // there's always only one currency (USD)
        previousEndingQuantity = tier.ending_quantity;
      }
    });

    return total;
  }
}

// trick: create interface with the same name as a class above so we don't
// need to redeclare the mmembeers
// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface PlanAddOn extends ServerPlanAddon {}

const DEFAULT_PLAN: Partial<ServerPlan> = {
  add_ons: [],
};

class Plan implements ServerPlan {
  constructor(attributes: Partial<ServerPlan> = DEFAULT_PLAN) {
    Object.assign(this, { ...DEFAULT_PLAN, ...attributes });
  }

  static parsePlan(rawPlan: ServerPlan): Plan {
    const addOns = (rawPlan.add_ons || []).map((rawAddOn) => new PlanAddOn(rawAddOn));
    return new Plan({ ...rawPlan, add_ons: addOns });
  }

  /**
   * Calculates period rate of the plan, depending in plan's billing interval
   * and taking into account provided addon quantity
   * @param {number} addonQuantity
   * @returns {number} Calculated price in USD.
   */
  calculatePeriodRate(addonQuantity: number): number {
    let basePrice = this.price;

    if (this.add_ons?.length) {
      basePrice = this.add_ons[0].calculatePrice(addonQuantity);
    }

    return basePrice;
  }

  /**
   * Calculates *monthly* rate of the plan, taking into account provided addon quantity
   * @param {number} addonQuantity
   * @returns {number} Calculated price in USD.
   */
  calculateMonthlyRate(addonQuantity: number): number {
    const periodRate = this.calculatePeriodRate(addonQuantity);

    if (this.interval_unit === PLAN_INTERVAL.YEAR) {
      return periodRate / 12;
    }

    return periodRate;
  }

  isUnified(): boolean {
    return this.plan_type === UNIFIED_BILLING_TYPE;
  }

  isUsage(): boolean {
    return this.plan_type === USAGE_TYPE;
  }

  isAvailableForUsageCustomers(): boolean {
    return (
      this.testing_type === TESTING_TYPES.API_TESTING ||
      this.testing_type === TESTING_TYPES.MOBILE_QUALITY
    );
  }

  displayName(): string {
    if (this.isUnified()) {
      let label = '';
      label = TESTING_TYPE_TO_LABEL[this.testing_type];
      const deviceType = DEVICE_TYPE_TO_LABEL[this.device_type];
      if (deviceType && this.testing_type !== PRODUCT_LIVE) {
        label += ` Testing - ${deviceType}`;
      }
      return label;
    }
    return this.name.replace(/\s-?\s?(Annual|Monthly)$/, '');
  }

  getPriceForCreditBlock(): number {
    if (this.isUsage() && this.add_ons.length) {
      const creditAddOn = this.add_ons.find(({ code }) => code === ADDON_CREDIT_BLOCKS);

      if (creditAddOn) {
        return creditAddOn.unit_amount * (this.interval_unit === PLAN_INTERVAL.YEAR ? 12 : 1) || 0;
      }
    }

    return 0;
  }

  creditsPerPeriod(creditBlocks: number): number {
    return creditsConsideringAnnualBonus(
      creditBlocks * CREDITS_PER_BLOCK,
      this.interval_unit === PLAN_INTERVAL.YEAR,
    );
  }

  isAPIT(): boolean {
    return getProductType(this) === PRODUCT_API_TESTING;
  }

  isMQ(): boolean {
    return getProductType(this) === PRODUCT_MOBILE_QUALITY;
  }

  isAPITorMQ(): boolean {
    return this.isAPIT() || this.isMQ();
  }
}

interface AutoTopUpToggleStatus {
  value: boolean
}

export { PlanAddOn, Plan, getProductType, AutoTopUpToggleStatus };
